import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiCheckCircle, PiLightbulbDuotone, PiCheckCircleDuotone } from "react-icons/pi";

export default function InfoSection({ content, onContentChange, isSelected }: SectionContentProps) {
    const layout = content.layout || "left-image";
    const cards = content.cards || [];
    const splitContent = content.splitContent || { left: {}, right: {} };

    // Dynamic styles
    const primaryColor = content.colorPalette?.primary || "var(--primary-color, #3B82F6)";
    const secondaryColor = content.colorPalette?.secondary || "var(--secondary-color, #4F46E5)";
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    // Helper for CSS var opacity
    const getOpacityColor = (color: string, opacity: number) => {
        if (color.startsWith('var')) return `color-mix(in srgb, ${color}, transparent ${100 - opacity * 100}%)`;
        return `${color}${Math.floor(opacity * 255).toString(16).padStart(2, '0')}`;
    };

    return (
        <div
            className={`relative ${isSelected ? "ring-2 ring-blue-500 rounded-lg" : ""}`}
            style={{ fontFamily }}
        >
            {/* Layout Switcher (Visible when selected) */}
            {isSelected && onContentChange && (
                <div className="absolute top-4 right-4 z-20 flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                    {["left-image", "right-image", "full-text", "full-image", "split"].map((l) => (
                        <button
                            key={l}
                            onClick={() => onContentChange({ ...content, layout: l })}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${layout === l ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                                }`}
                            style={layout === l ? { backgroundColor: getOpacityColor(primaryColor, 0.1), color: primaryColor } : {}}
                            title={l.replace("-", " ")}
                        >
                            <span className="capitalize">{l.replace("-", " ")}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* --- Left or Right Image Layout --- */}
            {(layout === "left-image" || layout === "right-image") && (
                <div className={`py-16 px-8 ${layout === "right-image" ? "bg-gray-50" : "bg-white"}`}>
                    <div className={`max-w-6xl mx-auto flex flex-col ${layout === "right-image" ? "md:flex-row-reverse" : "md:flex-row"} items-center gap-12`}>
                        <div className="flex-1 w-full relative group">
                            <img
                                src={content.image || "https://placehold.co/600x400"}
                                alt="Info"
                                className="rounded-2xl shadow-lg w-full h-auto object-cover max-h-[400px]"
                            />
                            {isSelected && (
                                <button
                                    className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded hover:bg-black/70"
                                    onClick={() => {
                                        const url = prompt("Enter image URL", content.image);
                                        if (url) onContentChange?.({ ...content, image: url });
                                    }}
                                >
                                    Change Image
                                </button>
                            )}
                        </div>
                        <div className="flex-1 space-y-6">
                            <div>
                                <h4
                                    className="font-semibold uppercase tracking-wider text-sm mb-2"
                                    style={{ color: primaryColor }}
                                >
                                    <EditableText value={content.subtitle} onChange={v => onContentChange?.({ ...content, subtitle: v })} as="span" placeholder="Subtitle" />
                                </h4>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                                    <EditableText value={content.title} onChange={v => onContentChange?.({ ...content, title: v })} as="span" placeholder="Main Title" />
                                </h2>
                            </div>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                <EditableText value={content.description} onChange={v => onContentChange?.({ ...content, description: v })} as="span" placeholder="Description text..." />
                            </p>

                            {/* Tags Editing */}
                            {(content.tags || []).length > 0 && (
                                <div className="flex flex-wrap gap-3 pt-4">
                                    {(content.tags || []).map((tag: any, idx: number) => {
                                        const tagText = typeof tag === 'string' ? tag : tag.tag || tag.text;
                                        return (
                                            <span
                                                key={idx}
                                                className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 shadow-sm"
                                                style={{ borderColor: getOpacityColor(primaryColor, 0.3) }}
                                            >
                                                <EditableText
                                                    value={tagText}
                                                    onChange={v => {
                                                        const newTags = [...(content.tags || [])];
                                                        if (typeof newTags[idx] === 'string') newTags[idx] = v;
                                                        else newTags[idx] = { ...newTags[idx], tag: v };
                                                        onContentChange?.({ ...content, tags: newTags });
                                                    }}
                                                    as="span"
                                                    placeholder="Tag"
                                                />
                                            </span>
                                        );
                                    })}
                                    {isSelected && (
                                        <button
                                            onClick={() => onContentChange?.({ ...content, tags: [...(content.tags || []), { tag: "New Tag" }] })}
                                            className="px-3 py-2 rounded-full border border-dashed border-gray-300 text-gray-500 hover:opacity-80 text-sm"
                                            style={{ color: primaryColor, borderColor: getOpacityColor(primaryColor, 0.5) }}
                                        >
                                            + Add
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Cards (Optional) */}
                            {layout === "left-image" && cards.length > 0 && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                                    {cards.map((card: any, idx: number) => (
                                        <div key={idx} className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                                            <h4 className="font-semibold text-gray-900 mb-1">
                                                <EditableText value={card.title} onChange={v => {
                                                    const newCards = [...cards]; newCards[idx] = { ...card, title: v };
                                                    onContentChange?.({ ...content, cards: newCards });
                                                }} as="span" placeholder="Card Title" />
                                            </h4>
                                            <p className="text-sm text-gray-500">
                                                <EditableText value={card.text} onChange={v => {
                                                    const newCards = [...cards]; newCards[idx] = { ...card, text: v };
                                                    onContentChange?.({ ...content, cards: newCards });
                                                }} as="span" placeholder="Card Text" />
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- Full Text Layout --- */}
            {layout === "full-text" && (
                <div className="py-20 px-8 bg-white text-center">
                    <div className="max-w-3xl mx-auto space-y-6">
                        <h4
                            className="font-semibold uppercase tracking-wider text-sm"
                            style={{ color: primaryColor }}
                        >
                            <EditableText value={content.subtitle} onChange={v => onContentChange?.({ ...content, subtitle: v })} as="span" placeholder="Subtitle" />
                        </h4>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                            <EditableText value={content.title} onChange={v => onContentChange?.({ ...content, title: v })} as="span" placeholder="Main Title" />
                        </h2>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            <EditableText value={content.description} onChange={v => onContentChange?.({ ...content, description: v })} as="span" placeholder="Description..." />
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8 text-left">
                            {cards.map((card: any, idx: number) => (
                                <div key={idx} className="p-6 rounded-2xl bg-white border border-gray-100 shadow-sm transition-hover hover:shadow-md">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                                        style={{ backgroundColor: getOpacityColor(primaryColor, 0.1), color: primaryColor }}
                                    >
                                        <PiCheckCircle className="w-6 h-6" />
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                                        <EditableText value={card.title} onChange={v => {
                                            const newCards = [...cards]; newCards[idx] = { ...card, title: v };
                                            onContentChange?.({ ...content, cards: newCards });
                                        }} as="span" placeholder="Title" />
                                    </h4>
                                    <p className="text-gray-600">
                                        <EditableText value={card.text} onChange={v => {
                                            const newCards = [...cards]; newCards[idx] = { ...card, text: v };
                                            onContentChange?.({ ...content, cards: newCards });
                                        }} as="span" placeholder="Text" />
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* --- Full Image / Split Layouts could be implemented similarly ... --- */}
            {layout === "full-image" && (
                <div className="relative py-24 px-8 bg-slate-900 text-white overflow-hidden text-center">
                    <div className="absolute inset-0">
                        <img src={content.image} alt="Background" className="w-full h-full object-cover opacity-40 hover:scale-105 transition-transform duration-[20s]" />
                        <div
                            className="absolute inset-0"
                            style={{ background: `linear-gradient(to top, ${getOpacityColor(secondaryColor, 0.9)}, transparent)` }}
                        ></div>

                    </div>
                    <div className="relative max-w-4xl mx-auto space-y-6">
                        <span
                            className="inline-block px-4 py-1 rounded-full text-sm font-medium backdrop-blur-sm"
                            style={{
                                backgroundColor: getOpacityColor(primaryColor, 0.3),
                                color: '#ffffff',
                                border: `1px solid ${getOpacityColor(primaryColor, 0.5)}`
                            }}
                        >
                            <EditableText value={content.subtitle} onChange={v => onContentChange?.({ ...content, subtitle: v })} as="span" placeholder="Tag" />
                        </span>
                        <h2 className="text-4xl md:text-6xl font-bold tracking-tight text-white drop-shadow-sm">
                            <EditableText value={content.title} onChange={v => onContentChange?.({ ...content, title: v })} as="span" placeholder="Main Title" />
                        </h2>
                        <p className="text-xl text-white/90 max-w-2xl mx-auto drop-shadow-sm">
                            <EditableText value={content.description} onChange={v => onContentChange?.({ ...content, description: v })} as="span" placeholder="Description..." />
                        </p>

                        {/* Buttons Rendering */}
                        {(content.buttons || []).length > 0 && (
                            <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                                {content.buttons.map((btn: any, idx: number) => (
                                    <a
                                        key={idx}
                                        href={btn.link || "#"}
                                        className={`px-8 py-3 rounded-full font-bold transition-all transform hover:-translate-y-1 shadow-lg ${btn.variant === 'outline'
                                            ? "bg-transparent border-2 text-white hover:bg-white/10"
                                            : "text-white"
                                            }`}
                                        style={btn.variant !== 'outline' ? {
                                            background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                                            boxShadow: `0 10px 20px -5px ${getOpacityColor(primaryColor, 0.4)}`
                                        } : { borderColor: "white" }}
                                    >
                                        <EditableText value={btn.text} onChange={v => {
                                            const newButtons = [...content.buttons];
                                            newButtons[idx] = { ...btn, text: v };
                                            onContentChange?.({ ...content, buttons: newButtons });
                                        }} as="span" placeholder="Button Text" />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {layout === "split" && (
                <div className="py-16 px-8 bg-white">
                    <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
                        {/* Left split */}
                        <div
                            className="p-8 rounded-3xl border flex flex-col justify-center items-start transition-all hover:shadow-lg"
                            style={{ backgroundColor: getOpacityColor(primaryColor, 0.08), borderColor: getOpacityColor(primaryColor, 0.2) }}
                        >
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                                style={{ backgroundColor: primaryColor, color: 'white' }}
                            >
                                <PiLightbulbDuotone className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                <EditableText value={splitContent.left?.title || "Title"} onChange={v => onContentChange?.({ ...content, splitContent: { ...splitContent, left: { ...splitContent.left, title: v } } })} as="span" placeholder="Left Title" />
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                <EditableText value={splitContent.left?.text || "Description"} onChange={v => onContentChange?.({ ...content, splitContent: { ...splitContent, left: { ...splitContent.left, text: v } } })} as="span" placeholder="Left Text" />
                            </p>
                        </div>
                        {/* Right split */}
                        <div
                            className="p-8 rounded-3xl border flex flex-col justify-center items-start transition-all hover:shadow-lg"
                            style={{ backgroundColor: getOpacityColor(secondaryColor, 0.08), borderColor: getOpacityColor(secondaryColor, 0.2) }}
                        >
                            <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg"
                                style={{ backgroundColor: secondaryColor, color: 'white' }}
                            >
                                <PiCheckCircleDuotone className="w-7 h-7" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">
                                <EditableText value={splitContent.right?.title || "Title"} onChange={v => onContentChange?.({ ...content, splitContent: { ...splitContent, right: { ...splitContent.right, title: v } } })} as="span" placeholder="Right Title" />
                            </h3>
                            <p className="text-gray-600 text-lg leading-relaxed">
                                <EditableText value={splitContent.right?.text || "Description"} onChange={v => onContentChange?.({ ...content, splitContent: { ...splitContent, right: { ...splitContent.right, text: v } } })} as="span" placeholder="Right Text" />
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
